import { useCallback } from 'react'

import { Button, Dropdown, Icon, Layout, Text } from '@loadsmart/miranda-react'

import { useAnalytics } from 'core/analytics/useAnalytics'

import { formatTrackingMethodName } from '../../presenters/preferredTrackingMethod'
import { useTrackingOnboardingContext } from './onboarding/TrackingOnboardingContext'
import { ProvidersBadge } from './ProvidersBadge'

export type PreferredTrackingMethodCardProps = {
  readonly trackingLink?: string | null
}

export function PreferredTrackingMethodCard({ trackingLink }: PreferredTrackingMethodCardProps) {
  const { track } = useAnalytics()
  const { preferredTrackingMethod, openTrackingConnectionDrawer } = useTrackingOnboardingContext()
  const { method, provider } = preferredTrackingMethod || {}

  const openExternalProviderLogs = useCallback(() => {
    if (trackingLink) {
      window.open(trackingLink, '_blank', 'noopener')?.focus()
      track('Opened: External provider logs', { source: 'dropdown' })
    }
  }, [track, trackingLink])

  const handleOpenTrackingConnectionDrawer = useCallback(() => {
    openTrackingConnectionDrawer()
    track('Opened: Tracking setup edit')
  }, [openTrackingConnectionDrawer, track])

  if (!method && !provider) {
    return null
  }

  const methods = method ? [method] : []
  const providers = provider ? [provider] : []

  return (
    <Layout.Box
      data-testid="preferred-tracking-method-card"
      backgroundColor="color-background-primary"
      borderColor="color-border"
      borderWidth="border-thin"
      borderRadius="border-radius-m"
      padding="spacing-2"
    >
      <Layout.Group justify="space-between">
        <Layout.Group align="center" gap="spacing-3">
          <ProvidersBadge methods={methods} providers={providers} />
          <Text variant="body-md-bold" color="color-primary-60">
            {formatTrackingMethodName(method)}
          </Text>
        </Layout.Group>
        <Layout.Group align="center" gap="spacing-2">
          <Text variant="body-sm" color="color-text-placeholder">
            Preferred method
          </Text>
          <Dropdown>
            <Button
              aria-label="Preferred method dropdown"
              variant="icon-secondary"
              size="small"
              aria-haspopup="true"
            >
              <Icon name="dots-vertical" size="20px" color="color-primary-60" />
            </Button>
            <Dropdown.Menu>
              <Dropdown.Item onClick={handleOpenTrackingConnectionDrawer}>
                Edit tracking method
              </Dropdown.Item>
              <Dropdown.Item disabled={!trackingLink} onClick={openExternalProviderLogs}>
                Open external provider logs
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Layout.Group>
      </Layout.Group>
    </Layout.Box>
  )
}
